// "Send from a wallet" (docs/08-app-design.md 5.4, 5.6, 5.7): a plain SOL
// transfer, built with the Wallet Standard, from any wallet the browser
// exposes to a destination this app names (the vault address or the fee
// reserve address). The wallet only ever signs a System Transfer; it never
// signs a vault instruction, and it never sees a one-time key.
//
// Playwright cannot drive a real browser-extension wallet, so this
// component's only automatable surface is its empty state ("No wallet
// found..."). Everything here is wrapped in an error boundary so a given
// wallet's own quirks - a feature shaped slightly differently than the spec,
// a `connect()` that throws synchronously - can never break the surrounding
// page; the boundary's fallback is the same "no wallet" sentence.

import {
  type Address,
  AccountRole,
  type Instruction,
  address,
  appendTransactionMessageInstruction,
  blockhash,
  compileTransaction,
  createTransactionMessage,
  getTransactionEncoder,
  pipe,
  setTransactionMessageFeePayer,
  setTransactionMessageLifetimeUsingBlockhash,
} from '@solana/kit';
import { getWallets } from '@wallet-standard/app';
import type { Wallet, WalletAccount } from '@wallet-standard/base';
import { Component, type ReactNode, useId, useState } from 'react';
import { type Rpc, toBase58 } from '../rpc';
import { Button, Field, StatusLine } from './primitives';

// ---------------------------------------------------------------------------
// Minimal Wallet Standard feature shapes. This app depends only on
// `@wallet-standard/app` (the registry), not on `@wallet-standard/features`
// or `@solana/wallet-standard-features`, so the two feature shapes it reads
// are declared here directly from the published, stable Wallet Standard
// spec rather than imported.
// ---------------------------------------------------------------------------

interface StandardConnectFeature {
  'standard:connect': {
    version: '1.0.0';
    connect(input?: { silent?: boolean }): Promise<{ accounts: readonly WalletAccount[] }>;
  };
}

interface SolanaSignAndSendTransactionFeature {
  'solana:signAndSendTransaction': {
    version: '1.0.0' | '1.1.0';
    signAndSendTransaction(...inputs: Array<{ account: WalletAccount; transaction: Uint8Array; chain: string }>): Promise<readonly { signature: Uint8Array }[]>;
  };
}

type SendableWallet = Omit<Wallet, 'features'> & { features: Wallet['features'] & StandardConnectFeature & SolanaSignAndSendTransactionFeature };

function isSendable(wallet: Wallet): wallet is SendableWallet {
  return 'standard:connect' in wallet.features && 'solana:signAndSendTransaction' in wallet.features;
}

/** The chain identifier a Wallet Standard wallet expects, from the app's own RPC setting. */
export function chainFor(rpcUrl: string): 'solana:mainnet' | 'solana:devnet' | 'solana:localnet' {
  let host = '';
  try {
    host = new URL(rpcUrl).hostname;
  } catch {
    return 'solana:mainnet';
  }
  if (host === 'localhost' || host === '127.0.0.1' || host === '[::1]') return 'solana:localnet';
  if (host.includes('devnet')) return 'solana:devnet';
  return 'solana:mainnet';
}

/**
 * Lamports from a decimal SOL string, with no floating-point rounding: the
 * string is split on its point and the fractional part is padded or
 * rejected, never passed through a float.
 */
export function solStringToLamports(input: string): bigint | null {
  const trimmed = input.trim();
  if (!/^\d+(\.\d+)?$/.test(trimmed)) return null;
  const [whole, frac = ''] = trimmed.split('.');
  if (frac.length > 9) return null;
  return BigInt(whole || '0') * 1_000_000_000n + BigInt(frac.padEnd(9, '0') || '0');
}

/** System program Transfer: u32le(2) || u64le(lamports) (mirrors sdk/ts/scripts/mainnet-canary.ts's systemTransfer helper). */
function systemTransferInstruction(from: Address, to: Address, lamports: bigint): Instruction {
  const data = new Uint8Array(12);
  new DataView(data.buffer).setUint32(0, 2, true);
  new DataView(data.buffer).setBigUint64(4, lamports, true);
  return { programAddress: address('11111111111111111111111111111111'), accounts: [{ address: from, role: AccountRole.WRITABLE_SIGNER }, { address: to, role: AccountRole.WRITABLE }], data };
}

type SendState = { kind: 'idle' } | { kind: 'connecting' } | { kind: 'sending' } | { kind: 'sent'; signature: string } | { kind: 'failed'; error: string };

class WalletErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { failed: false };
  }
  static getDerivedStateFromError() {
    return { failed: true };
  }
  componentDidCatch(error: unknown) {
    console.error('wallet-standard integration did not render', error);
  }
  render() {
    if (this.state.failed) return <p className="ink">No wallet found. Send from any wallet to the address instead.</p>;
    return this.props.children;
  }
}

/** The button shown before anything is picked; "no wallet" is decided once, up front, rather than after a click, and needs no boundary of its own since discovery is wrapped in its own try/catch. */
function WalletSendGate(props: { destination: string; rpc: Rpc; rpcUrl: string; onSent: (signature: string) => void }) {
  const [wallets] = useState<SendableWallet[]>(() => {
    try {
      return getWallets().get().filter(isSendable);
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);
  if (wallets.length === 0) return <p className="ink">No wallet found. Send from any wallet to the address instead.</p>;
  if (!open) {
    return (
      <div className="actions">
        <Button variant="ghost" small onClick={() => setOpen(true)}>
          Send from a wallet
        </Button>
      </div>
    );
  }
  return <WalletSendForm {...props} wallets={wallets} />;
}

function WalletSendForm({ destination, rpc, rpcUrl, onSent, wallets }: { destination: string; rpc: Rpc; rpcUrl: string; onSent: (signature: string) => void; wallets: SendableWallet[] }) {
  const [walletIndex, setWalletIndex] = useState(0);
  const [account, setAccount] = useState<WalletAccount | null>(null);
  const [amount, setAmount] = useState('');
  const [state, setState] = useState<SendState>({ kind: 'idle' });
  const wallet = wallets[walletIndex]!;
  const walletChoiceId = useId();
  const amountId = useId();

  const connect = async () => {
    setState({ kind: 'connecting' });
    try {
      const { accounts } = await wallet.features['standard:connect'].connect();
      const first = accounts[0];
      if (!first) throw new Error('The wallet did not authorise an account.');
      setAccount(first);
      setState({ kind: 'idle' });
    } catch (e) {
      setState({ kind: 'failed', error: e instanceof Error ? e.message : String(e) });
    }
  };

  const send = async () => {
    if (!account) return;
    const lamports = solStringToLamports(amount);
    if (lamports === null || lamports <= 0n) {
      setState({ kind: 'failed', error: 'Enter an amount in SOL.' });
      return;
    }
    setState({ kind: 'sending' });
    try {
      const bh = await rpc.getLatestBlockhash();
      const fromAddress = address(account.address);
      const message = pipe(
        createTransactionMessage({ version: 0 }),
        (m) => setTransactionMessageFeePayer(fromAddress, m),
        (m) => setTransactionMessageLifetimeUsingBlockhash({ blockhash: blockhash(toBase58(bh.blockhash)), lastValidBlockHeight: bh.lastValidBlockHeight }, m),
        (m) => appendTransactionMessageInstruction(systemTransferInstruction(fromAddress, address(destination), lamports), m),
      );
      const wire = new Uint8Array(getTransactionEncoder().encode(compileTransaction(message)));
      const [result] = await wallet.features['solana:signAndSendTransaction'].signAndSendTransaction({ account, transaction: wire, chain: chainFor(rpcUrl) });
      if (!result) throw new Error('The wallet returned no result.');
      const signature = toBase58(result.signature);
      setState({ kind: 'sent', signature });
      onSent(signature);
    } catch (e) {
      setState({ kind: 'failed', error: e instanceof Error ? e.message : String(e) });
    }
  };

  return (
    <div className="stack tight">
      {wallets.length > 1 && !account ? (
        <Field id={walletChoiceId} label="Wallet">
          <select id={walletChoiceId} className="input" value={walletIndex} onChange={(e) => setWalletIndex(Number(e.target.value))}>
            {wallets.map((w, i) => (
              <option key={w.name} value={i}>
                {w.name}
              </option>
            ))}
          </select>
        </Field>
      ) : null}
      {!account ? (
        <div className="actions">
          <Button small onClick={() => void connect()} disabled={state.kind === 'connecting'} working={state.kind === 'connecting'}>
            {state.kind === 'connecting' ? 'Connecting' : `Connect ${wallet.name}`}
          </Button>
        </div>
      ) : (
        <>
          <p className="ink">
            Connected: {account.address.slice(0, 4)}…{account.address.slice(-4)} ({wallet.name}).
          </p>
          <Field id={amountId} label="Amount (SOL)">
            <input id={amountId} className="input" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" disabled={state.kind === 'sending'} />
          </Field>
          <div className="actions">
            <Button small onClick={() => void send()} disabled={state.kind === 'sending' || !amount} working={state.kind === 'sending'}>
              {state.kind === 'sending' ? 'Sending' : 'Send'}
            </Button>
          </div>
        </>
      )}
      {state.kind === 'sent' ? (
        <StatusLine state="pending" word="Sent.">
          Signature {state.signature.slice(0, 6)}…{state.signature.slice(-6)}. Checking the chain for it now.
        </StatusLine>
      ) : null}
      {state.kind === 'failed' ? (
        <StatusLine state="failed" word="Not completed.">
          {state.error}
        </StatusLine>
      ) : null}
    </div>
  );
}

/**
 * "Send from a wallet" (docs/08 5.4, 5.7): a "Send from a wallet" button that
 * opens an inline connect-then-send panel, next to "I sent it" / the fee
 * reserve's own top-up controls. If no wallet is found the button never
 * appears; the whole thing sits inside an error boundary so a wallet's own
 * API mismatch can never take the surrounding page down with it.
 */
export function WalletSend(props: { destination: string; rpc: Rpc; rpcUrl: string; onSent: (signature: string) => void }) {
  return (
    <WalletErrorBoundary>
      <WalletSendGate {...props} />
    </WalletErrorBoundary>
  );
}

// ---------------------------------------------------------------------------
// "Use my wallet address" (docs/08 5.6): the withdraw destination field's
// wallet-standard shortcut. Connects the first wallet the browser exposes
// and hands its first account's address back to the caller; the wallet
// signs nothing here. Renders nothing when no wallet is found, and needs no
// boundary of its own beyond the one below, for the same reason
// WalletSendGate needs none: discovery is wrapped in its own try/catch.
// ---------------------------------------------------------------------------

function WalletAddressGate({ onAddress }: { onAddress: (address: string) => void }) {
  const [wallets] = useState<SendableWallet[]>(() => {
    try {
      return getWallets().get().filter(isSendable);
    } catch {
      return [];
    }
  });
  const [state, setState] = useState<'idle' | 'connecting' | 'failed'>('idle');
  const [error, setError] = useState<string | null>(null);
  if (wallets.length === 0) return null;
  const wallet = wallets[0]!;

  const connect = async () => {
    setState('connecting');
    try {
      const { accounts } = await wallet.features['standard:connect'].connect();
      const first = accounts[0];
      if (!first) throw new Error('The wallet did not authorise an account.');
      setState('idle');
      onAddress(first.address);
    } catch (e) {
      setState('failed');
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div className="stack tight">
      <div className="actions">
        <Button variant="ghost" small type="button" onClick={() => void connect()} disabled={state === 'connecting'} working={state === 'connecting'}>
          {state === 'connecting' ? 'Connecting' : 'Use my wallet address'}
        </Button>
      </div>
      {state === 'failed' ? (
        <StatusLine state="failed" word="Could not connect.">
          {error}
        </StatusLine>
      ) : null}
    </div>
  );
}

/**
 * "Use my wallet address" (docs/08 5.6): connects a Wallet Standard wallet
 * and hands its first account's address to `onAddress`, for a destination
 * field. Renders nothing when no wallet is found; wrapped in the same error
 * boundary as WalletSend so a wallet's own quirks cannot break the
 * surrounding page. Playwright cannot drive a real extension wallet, so this
 * component's only automatable surface is its empty state, same as WalletSend.
 */
export function WalletAddressButton({ onAddress }: { onAddress: (address: string) => void }) {
  return (
    <WalletErrorBoundary>
      <WalletAddressGate onAddress={onAddress} />
    </WalletErrorBoundary>
  );
}
